import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import Loading from '../../../../components/Loading';
import { useAtom } from 'jotai';
import { userDataAtom } from '../../../../AtomStore';
import DeletePostingConfirmModal from './DeletePostingConfirmModal';

interface PostProps {
  id: number;
  title: string;
  writer: string;
  date: string;
  church: string;
  religiousbody: string;
  location: string;
  sort: string;
  recruitNum: string;
  customInput: string;
  tableType?: string; // 'minister', 'church', 'institute'
  status?: 'OPEN' | 'CLOSED';
}

export default function PostingManagePanel() {
  let navigate = useNavigate();
  const [userData] = useAtom(userDataAtom);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [userPosts, setUserPosts] = useState<PostProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; count: number; tableType: string } | null>(null);
  const [submittingDelete, setSubmittingDelete] = useState<boolean>(false);

  // 사용자 게시글 가져오기
  const fetchUserPosts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${MainURL}/mypage/getuserposts/${userData.userAccount}`);
      if (Array.isArray(res.data)) {
        setUserPosts(res.data);
      } else {
        setUserPosts([]);
      }
    } catch (error) {
      setUserPosts([]);
      console.error('게시글 가져오기 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserPosts();
  }, [refresh]);

  // 게시글 삭제 요청: 채용지원자(국내/해외 사역자 공고)가 있으면 확인 모달로, 없으면 즉시 삭제
  const handleDeletePost = async (postId: number, tableType?: string) => {
    const type = tableType || 'minister';

    if (type !== 'minister' && type !== 'overseas') {
      if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
        await deletePost(postId, type);
      }
      return;
    }

    try {
      const res = await axios.get(`${MainURL}/applications/recruitapplicationcount/${postId}`, {
        params: { recruitType: type },
      });
      if (!res.data.success) throw new Error(res.data.error);

      if (res.data.count === 0) {
        if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
          await deletePost(postId, type);
        }
        return;
      }

      setPendingDelete({ id: postId, count: res.data.count, tableType: type });
    } catch (error) {
      console.error('지원자 수 조회 실패:', error);
      alert('삭제 처리 중 오류가 발생했습니다.');
    }
  };

  const deletePost = async (postId: number, tableType: string) => {
    try {
      const res = await axios.post(`${MainURL}/mypage/deletepost`, {
        postId: postId,
        userAccount: userData.userAccount,
        tableType: tableType,
      });
      if (res.data) {
        alert('게시글이 삭제되었습니다.');
        setRefresh(!refresh);
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('삭제 실패:', error);
      alert('삭제에 실패했습니다.');
    }
  };

  const closePost = async (postId: number, tableType: string) => {
    try {
      const res = await axios.post(`${MainURL}/mypage/closepost`, {
        postId: postId,
        userAccount: userData.userAccount,
        tableType: tableType,
      });
      if (res.data) {
        alert('공고가 마감 처리되었습니다.');
        setRefresh(!refresh);
      } else {
        alert('마감 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('마감 처리 실패:', error);
      alert('마감 처리에 실패했습니다.');
    }
  };

  const handleConfirmDeletePosting = async () => {
    if (!pendingDelete) return;
    setSubmittingDelete(true);
    await deletePost(pendingDelete.id, pendingDelete.tableType);
    setSubmittingDelete(false);
    setPendingDelete(null);
  };

  const handleConfirmClosePosting = async () => {
    if (!pendingDelete) return;
    setSubmittingDelete(true);
    await closePost(pendingDelete.id, pendingDelete.tableType);
    setSubmittingDelete(false);
    setPendingDelete(null);
  };

  // 게시글 수정 페이지로 이동
  const handleEditPost = (postId: number, tableType?: string) => {
    const type = tableType || 'minister';
    navigate(`/mypage/postingedit?id=${postId}&tableType=${type}`);
    window.scrollTo(0, 0);
  };

  // 게시글 상세보기
  const handleViewPost = (postId: number, tableType?: string) => {
    if (tableType === 'church') {
      navigate(`/recruit/church/${postId}`);
    } else if (tableType === 'institute') {
      navigate(`/recruit/institute/${postId}`);
    } else if (tableType === 'overseas') {
      navigate(`/recruit/overseas/${postId}`);
    } else {
      navigate(`/recruit/minister/${postId}`);
    }
    window.scrollTo(0, 0);
  };

  return (
    <div className="subpage__main__content" style={{ marginTop: 18 }}>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Loading />
        </div>
      ) : userPosts.length > 0 ? (
        <div className="postingList">
          {userPosts.map((post) => {
            const categoryName = post.tableType === 'church'
              ? '찬양대/방송/직원'
              : post.tableType === 'institute'
              ? '학교/기관/단체'
              : post.tableType === 'overseas'
              ? '해외사역지'
              : '국내사역지';

            return (
              <div key={post.id} className="postingItem">
                <div className="postingHeader">
                  <div className="postingTitle">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                      <span className="categoryTag">{categoryName}</span>
                      <h3 style={{ margin: 0 }}>{post.title}</h3>
                      {post.status === 'CLOSED' && <span className="categoryTag">마감</span>}
                    </div>
                    <span className="postingDate">{post.date}</span>
                  </div>
                  <div className="postingActions">
                    {(!post.tableType || post.tableType === 'minister' || post.tableType === 'overseas') && (
                      <button
                        className="actionBtn applicantBtn"
                        onClick={() => navigate(`/mypage/applicantmanage?id=${post.id}&tableType=${post.tableType || 'minister'}`)}
                      >
                        지원자보기
                      </button>
                    )}
                    <button
                      className="actionBtn viewBtn"
                      onClick={() => handleViewPost(post.id, post.tableType)}
                    >
                      보기
                    </button>
                    <button
                      className="actionBtn editBtn"
                      onClick={() => handleEditPost(post.id, post.tableType)}
                    >
                      수정
                    </button>
                    <button
                      className="actionBtn deleteBtn"
                      onClick={() => handleDeletePost(post.id, post.tableType)}
                    >
                      삭제
                    </button>
                  </div>
                </div>
                <div className="postingInfo">
                  <div className="infoRow">
                    <span className="infoLabel">교회명:</span>
                    <span className="infoValue">{post.church}</span>
                  </div>
                  <div className="infoRow">
                    <span className="infoLabel">직무:</span>
                    <span className="infoValue">{post.sort}</span>
                  </div>
                </div>
                {post.customInput && (
                  <div className="postingContent">
                    <div
                      className="contentPreview"
                      dangerouslySetInnerHTML={{
                        __html: post.customInput.length > 200
                          ? post.customInput.substring(0, 200) + '...'
                          : post.customInput
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="noPosts">
          <p>작성한 게시글이 없습니다.</p>
        </div>
      )}

      {pendingDelete && (
        <DeletePostingConfirmModal
          applicantCount={pendingDelete.count}
          onClose={() => setPendingDelete(null)}
          onConfirmClosePosting={handleConfirmClosePosting}
          onConfirmDeletePosting={handleConfirmDeletePosting}
          submitting={submittingDelete}
        />
      )}
    </div>
  );
}
